import { QUERY_PARAMS } from "./query-params";

export const ATS_PUBLIC_APP_PATH = "job-board";

let companyId: string | null = null;
/**
 * Route resolver factory that creates dynamic route functions with automatic app prefix handling.
 *
 * This replaces static route strings with a function-based approach that supports:
 * - Automatic app prefix prepending (e.g., "/job-board/profile" vs "profile")
 * - Dynamic path parameters (e.g., ":companyId" or actual values)
 * - Relative vs absolute path generation for different routing contexts
 *
 * @param route - The base route path (e.g., "/profile")
 * @returns Function that generates the final route with optional parameters
 *
 * Usage:
 * - jobsRoutes.jobs.path({ pathParam: "company-123" }) → "/job-board/company-123" (absolute)
 * - jobsRoutes.jobs.path({ isRelative: true, pathParam: ":companyId" }) → "/:companyId" (relative)
 * - jobsRoutes.jobs.jobPostId.path({ pathParam: ["company-123", "post-456"] }) →
 *   "/job-board/company-123/post-456"
 */
const resolveRoute =
  (route: string = "", suffixRoute = "") =>
  (params?: { isRelative?: boolean; pathParam?: string | string[] }) => {
    const { isRelative = false, pathParam = null } = params ?? {};

    if (companyId === null) {
      throw new Error("companyId not found in URL");
    }

    let finalPath = isRelative ? route : `/${ATS_PUBLIC_APP_PATH}/${companyId}` + route;

    if (pathParam) {
      finalPath += "/" + (Array.isArray(pathParam) ? pathParam.join("/") : pathParam);
    }

    if (suffixRoute) {
      finalPath += "/" + suffixRoute;
    }

    return finalPath;
  };

export const jobsRoutes = {
  home: {
    path: resolveRoute(),
    view: {
      path: resolveRoute("", "view"),
      supportedSearchParams: { locale: QUERY_PARAMS.locale },
    },
    apply: {
      path: resolveRoute("", "apply"),
      supportedSearchParams: { locale: QUERY_PARAMS.locale },
    },
  },

  notFound: {
    path: resolveRoute("/not_found"),
  },
  forbidden: {
    path: resolveRoute("/forbidden"),
  },
};

export const useJobRouteConfig = (companyIdOverride?: string) => {
  if (companyIdOverride) {
    companyId = companyIdOverride;

    return jobsRoutes;
  }

  const pathNames = window.location.pathname.split("/").filter(Boolean);

  const idx = pathNames.indexOf(ATS_PUBLIC_APP_PATH);
  const companyIdFromUrl = idx >= 0 ? pathNames[idx + 1] : null;

  if (!companyIdFromUrl) {
    throw new Error("companyId not found in URL");
  }

  companyId = pathNames[idx + 1];

  return jobsRoutes;
};
